import {
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    TextField
} from '@mui/material';
import React from 'react';
import {
    useCreateChatMessageMutation,
    useGetChatProfileQuery,
    useGetChatUserQuery
} from '@store/feature/chat/chatApi';
import { useParams } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

const SendChat = ({ onMessageSent = () => {} }) => {
    const { id } = useParams();
    const [createChatMessage] = useCreateChatMessageMutation();
    const { data: profile } = useGetChatProfileQuery();

    const { data: userProfile } = useGetChatUserQuery(id);

    const removeImage = (index) => {
        setMyMessage((prev) => ({
            ...prev,
            files: Array.from(prev.files).filter((_, i) => i !== index)
        }));
    };


    // Load use_knowledge_base from localStorage if available
    const getInitialKnowledgeBase = () => {
        const stored = localStorage.getItem('use_knowledge_base');
        return stored === 'true';
    };

    const [myMessage, setMyMessage] = React.useState({
        body: '',
        from: profile?.uuid,
        to: id,
        files: [],
        use_knowledge_base: getInitialKnowledgeBase()
    });


    const handleUseKnowledgeBaseChange = (event) => {
        const checked = event.target.checked;
        setMyMessage((prev) => ({
            ...prev,
            use_knowledge_base: checked
        }));
        localStorage.setItem('use_knowledge_base', checked);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            handleSendMessage(event);
        }
    };

    // onChange should read the value directly
    const handleChange = (event) => {
        if (event.key === 'Enter' && event.shiftKey) {
            setMyMessage((prev) => ({
                ...prev,
                body: event.target.value + '\n'
            }));
        } else {
            setMyMessage((prev) => ({ ...prev, body: event.target.value }));
        }
    };

    const handleFileChange = (e) => {
        setMyMessage({
            ...myMessage,
            files: e.target.files
        });
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        try {
            if (!myMessage?.body && myMessage?.files.length === 0) {
                return;
            }

            const message = {
                ...myMessage,
                body: myMessage?.body.trim(),
                to: id,
                files: myMessage?.files,
                use_knowledge_base: myMessage?.use_knowledge_base || false
            };

            setMyMessage((prev) => ({
                ...prev,
                body: '',
                to: id,
                files: []
                // Do not reset use_knowledge_base, keep its value from state/localStorage
            }));

            const formData = new FormData();
            formData.append('body', message?.body);
            formData.append('to', message?.to);
            formData.append('from', message?.from);
            formData.append('use_knowledge_base', message?.use_knowledge_base);

            if (message?.files) {
                for (let i = 0; i < message?.files.length; i++) {
                    formData.append('files', message?.files[i]);
                }
            }

            // Prefer unwrap() so we get the created message directly or catch an error.
            try {
                const serverMessage = await createChatMessage(formData).unwrap();

                // Only add the server message, not the optimistic one
                if (serverMessage && serverMessage.uuid) {
                    onMessageSent(serverMessage);
                }
            } catch (err) {
                console.error('Failed to send message (unwrap):', err);
                // Fallback to optimistic update so UI still shows the message immediately
                onMessageSent({
                    uuid: 'temp-' + Date.now().toString(),
                    body: message?.body,
                    from: profile?.uuid,
                    to: id,
                    created_at: new Date().toISOString(),
                    files: message?.files ? Array.from(message?.files) : [],
                    status: 'sent'
                });
            }
        } catch (error) {
            console.log(error, 'Error in handleSendMessage');
        }
    };

    return (
        <Box
            sx={{
                maxHeight: '500px',
                minHeight: '64px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                m: '8px'
            }}
            component="form"
            onSubmit={handleSendMessage}
        >
            {/* text field */}
            <TextField
                fullWidth
                size="small"
                name="body"
                variant="standard"
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                value={myMessage?.body}
                placeholder={`Message ${userProfile?.user?.fullname}`}
                InputProps={{
                    disableUnderline: true // <== added this
                }}
                multiline
                sx={{
                    px: '8px',
                    pt: '8px',
                    width: '100%',
                    maxHeight: '100px',
                    minHeight: '64px',
                    overflowY: 'auto'
                }}
            />
            {/* end of text field */}
            {/* selected image */}
            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    maxHeight: '200px',
                    px: '8px',
                    mt: '8px'
                }}
            >
                {Array.from(myMessage?.files)?.map((file, index) => (
                    <Box
                        key={index}
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <img
                            src={URL.createObjectURL(file)}
                            alt="preview"
                            style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '8px',
                                objectFit: 'cover'
                            }}
                        />
                        <Box
                            onClick={() => removeImage(index)}
                            sx={{
                                position: 'absolute',
                                top: '0',
                                right: '0',
                                transform: ' translate(50%, -50%)',
                                backgroundColor: 'rgba(0,0,0,0.8)',
                                color: 'white',
                                borderRadius: '50%',
                                padding: '4px',
                                width: '15px',
                                height: '15px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer'
                            }}
                            title="Remove File"
                        >
                            <CloseIcon
                                sx={{
                                    fontSize: '0.8rem'
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
            {/* end of selected image */}
            <Box
                sx={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '8px'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexGrow: 1
                    }}
                >
                    {/* select image */}
                    <input
                        color="primary"
                        type="file"
                        name="files"
                        onChange={handleFileChange}
                        style={{
                            display: 'none'
                        }}
                        id="file"
                        multiple
                    />
                    {/* {userProfile?.user?.fullname !== 'Chatbot' && (
                        <InputLabel htmlFor="file">
                            <IconButton
                                color="primary"
                                component="span"
                            >
                                <AddPhotoAlternateIcon fontSize="small" />
                            </IconButton>
                        </InputLabel>
                    )} */}
                    {/* end of select image */}
                    {/* use knowledge base */}
                    {userProfile?.user?.fullname === 'Chatbot' && (
                        // checkbox for use knowledge base
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={myMessage?.use_knowledge_base}
                                    color="primary"
                                    size="small"
                                    onChange={handleUseKnowledgeBaseChange}
                                />
                            }
                            label="Use Knowledge Base"
                            sx={{
                                marginLeft: '2px',
                                marginRight: '0',
                                fontSize: '0.875rem',
                                color: '#666',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.875rem',
                                    color: '#666',
                                }
                            }}
                        />
                    )}
                </Box>

                {/* send button */}
                <IconButton color="primary" type="submit">
                    <SendIcon fontSize="small" />
                </IconButton>
                {/* end of send button */}
            </Box>
        </Box>
    );
};

export default SendChat;
